import { useCallback, useEffect, useReducer, useRef } from 'react'
import { onBeforeAuthReset } from '../auth/session'
import type { Client } from '../data/clients'
import { deleteCompanyCreation, putCompanyCreation, type CreationResult } from '../data/companies'
import {
  isCreationDropped, loadCreationAttempts, pruneOrphanFacts, recordCreationAttempt,
  removeCreationAttempt, setCreationFact, type CreationFact, type StoredAttempt,
} from '../lib/preferences'

/**
 * One attempt the switcher shows (GRO-82 design, D6). *Pending here*: a send of its key is
 * in flight in this page. *Elsewhere*: another page most likely still waits on its first
 * answer. *Unresolved*: no answer settled it, and the reader may try again or cancel.
 */
export interface CreationBlock {
  key: string
  name: string
  state: 'pendingHere' | 'elsewhere' | 'unresolved'
  /** The last send answered 404 while the key was uncertain: the not-available line. */
  oldBackend: boolean
  /** Storage did not keep this attempt or one of its facts: the keep-this-page-open line. */
  keepOpen: boolean
  /** A read of the key's drop failed, so Try again sent nothing. */
  checkFailed: boolean
}

export type CreationNotice =
  | 'unavailable'
  | 'notListed'
  | 'refreshFailed'
  | 'checkFailedCreated'
  | 'alreadyCreated'
  | 'cancelled'
  | 'inventoryFailed'
  /** A dropped or settled attempt whose facts storage refused: its protection lasts as long as the page. */
  | 'keepOpen'

/** The notices that "Check again" answers. */
export const CHECK_AGAIN_NOTICES: readonly CreationNotice[] = ['notListed', 'refreshFailed', 'checkFailedCreated', 'inventoryFailed']

export interface CreationPanelState {
  blocks: CreationBlock[]
  notices: CreationNotice[]
  /** Whether "Add client" is offered: no unresolved attempt, and a known inventory (D4). */
  canAdd: boolean
  /** The inventory stayed unreadable on two reads: "Add anyway" with its warning (D4). */
  offerAnyway: boolean
  /**
   * The name a create from this page was refused for as a duplicate (GRO-130, D6): the
   * panel reopens the form with it and the error under the field, then calls
   * `onDuplicateShown`. The refused key is spent; the next Create mints a new one.
   */
  duplicateName: string | null
}

export interface CreationHandlers {
  onCreate: (name: string) => void
  onRetry: (key: string) => void
  onCancel: (key: string) => void
  onCheckAgain: () => void
  onAddAnyway: () => void
  onDuplicateShown: () => void
}

export interface CompanyCreation extends CreationHandlers {
  panel: CreationPanelState
}

export const IDLE_CREATION: CompanyCreation = {
  panel: { blocks: [], notices: [], canAdd: true, offerAnyway: false, duplicateName: null },
  onCreate: () => {},
  onRetry: () => {},
  onCancel: () => {},
  onCheckAgain: () => {},
  onAddAnyway: () => {},
  onDuplicateShown: () => {},
}

export interface UseCompanyCreationOptions {
  /** Without one, attempts live in this page's memory only and storage is never touched. */
  userId?: string
  refreshClients: () => Promise<Client[] | null>
  /** Whether the switcher is the open panel; each change from closed to open reconciles. */
  switcherOpen: boolean
  /** A company confirmed for a key the reader pressed Create or Try again on in this page. */
  onCreated: (companyId: string) => void
  /** Off for a guest (GRO-83): no storage, channel, teardown callback or inventory read. */
  enabled?: boolean
}

/** How long a send stays "pending" before the attempt shows as unresolved (D2). */
export const CREATE_PENDING_MS = 15_000
/** A cancel try's deadline, the gap before the next and the tries in one burst (D4). */
export const CANCEL_SEND_MS = 10_000
export const CANCEL_GAP_MS = 5_000
export const CANCEL_TRIES = 3
/** Another page's attempt shows as "being added elsewhere" until it is this old (D4). */
export const ELSEWHERE_MS = 30_000

type Outcome = StoredAttempt['outcome']

/** Everything this page knows about one key: the store's facts plus what only it can know. */
interface Attempt {
  key: string
  name: string
  at: number
  /** The record was stored, by this page or another, or learned from storage or the channel (D3). */
  shared: boolean
  mintedHere: boolean
  /** For a `404`: this page's own knowledge, and always true for a key it did not mint (D2). */
  uncertain: boolean
  /** An `uncertain.<key>` fact is known to exist: display only (D4). */
  storedUncertain: boolean
  dropped: boolean
  outcome: Outcome
  /** The reader pressed Create or Try again for it in this page (D6). */
  eligible: boolean
  /** The current `PUT`, its pending timer, and whether the timer fired for it. */
  put: AbortController | null
  pendingFired: boolean
  /** The one `DELETE` this page holds open for the key, if any. */
  del: AbortController | null
  oldBackend: boolean
  checkFailed: boolean
  /** Facts storage refused, kept here and retried at the next reconciliation. */
  unstored: CreationFact[]
  /** Private, or a fact went unstored: the keep-this-page-open line (D3). */
  degraded: boolean
  /** This page's completion of a company answer (D6). */
  completion: null | { companyId: string; stage: 'refreshing' | 'failed' | 'lacks' | 'held' | 'done' }
  /** The record is gone from the store; the entry stays only for a local completion. */
  settled: boolean
}

type Inventory = 'known' | 'unknown'
/** The channel's envelope (D3): validated on receipt, dropped when it names another user. */
type MessageBody =
  | { type: 'attempt'; name: string; at: number }
  | { type: 'fact'; fact: 'uncertain' | 'dropped' | 'void' }
  | { type: 'company'; companyId: string }
  | { type: 'removed' }
type Message = { v: 1; userId: string; key: string } & MessageBody

function messageOf(value: unknown, userId: string): Message | null {
  if (typeof value !== 'object' || value === null) return null
  const m = value as Record<string, unknown>
  if (m.v !== 1 || m.userId !== userId || typeof m.key !== 'string' || m.key === '') return null
  switch (m.type) {
    case 'attempt':
      return typeof m.name === 'string' && typeof m.at === 'number' ? (m as unknown as Message) : null
    case 'fact':
      return m.fact === 'uncertain' || m.fact === 'dropped' || m.fact === 'void' ? (m as unknown as Message) : null
    case 'company':
      return typeof m.companyId === 'string' && m.companyId !== '' ? (m as unknown as Message) : null
    case 'removed':
      return m as unknown as Message
    default:
      return null
  }
}

function blank(key: string, name: string, at: number): Attempt {
  return {
    key, name, at, shared: true, mintedHere: false, uncertain: false, storedUncertain: false, dropped: false,
    outcome: null, eligible: false, put: null, pendingFired: false, del: null, oldBackend: false,
    checkFailed: false, unstored: [], degraded: false, completion: null, settled: false,
  }
}

/**
 * The add-client attempt machine (GRO-82 design, D2-D6). App owns it, not the panel: the
 * panel can close while a request runs, and the attempt must outlive it.
 *
 * The page's view of every key lives in one map in a ref, so the asynchronous paths (an
 * answer, a timer, another tab's message) always act on the current state; a counter
 * re-renders after each change. Storage and the channel carry facts between pages (D3);
 * the map adds what only this page knows: whether it sent the key, whether the reader
 * pressed a button for it here, and its own completion.
 */
export function useCompanyCreation({
  userId, refreshClients, switcherOpen, onCreated, enabled = true,
}: UseCompanyCreationOptions): CompanyCreation {
  const [, rerender] = useReducer((n: number) => n + 1, 0)
  const attempts = useRef(new Map<string, Attempt>())
  const inventory = useRef<Inventory>('known')
  const inventoryFailures = useRef({ opening: false, checkAgain: false })
  const anyway = useRef(false)
  const notices = useRef(new Set<CreationNotice>())
  /**
   * Keys whose drop storage refused, kept after their attempt settles or leaves memory: the
   * tombstone is what a stale page reads before it retries or selects (D3), so it is owed
   * until a reconciliation stores it, and the page says to stay open until then.
   */
  const owedTombstones = useRef(new Set<string>())
  /** Every request the hook has open, including those of keys already gone from memory. */
  const owned = useRef(new Set<AbortController>())
  /** Keys this page saw settle, drop or void: a late `attempt` message never brings one back (D3). */
  const ended = useRef(new Set<string>())
  /**
   * Every drop this page has heard, kept apart from the attempts in memory: a drop can arrive
   * for a key this page has not loaded yet (its reads failing), and must still hold when the
   * attempt is loaded later, for retries and completions alike (D4, D6).
   */
  const heardDropped = useRef(new Set<string>())
  const refreshFailed = useRef(false)
  const duplicateName = useRef<string | null>(null)
  const channel = useRef<BroadcastChannel | null>(null)
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>())
  const live = useRef(true)

  const refreshRef = useRef(refreshClients)
  refreshRef.current = refreshClients
  const onCreatedRef = useRef(onCreated)
  onCreatedRef.current = onCreated
  const userRef = useRef(userId ?? '')
  userRef.current = userId ?? ''
  const openRef = useRef(switcherOpen)
  openRef.current = switcherOpen

  const later = useCallback((ms: number, run: () => void) => {
    const id = setTimeout(() => {
      timers.current.delete(id)
      if (live.current) run()
    }, ms)
    timers.current.add(id)
    return id
  }, [])
  const cancelTimer = (id: ReturnType<typeof setTimeout> | null) => {
    if (id === null) return
    clearTimeout(id)
    timers.current.delete(id)
  }

  const post = (a: Attempt, message: MessageBody) => {
    if (!a.shared || userRef.current === '') return
    try {
      channel.current?.postMessage({ v: 1, userId: userRef.current, key: a.key, ...message })
    } catch {
      // the channel is closed or missing; storage still carries the fact
    }
  }

  /** A blind write of one fact, and its broadcast. A shared attempt only; a private one is memory. */
  const writeFact = (a: Attempt, fact: CreationFact) => {
    if (!a.shared || userRef.current === '') return
    if (!setCreationFact(userRef.current, a.key, fact)) a.unstored.push(fact)
    if (typeof fact === 'object') post(a, { type: 'company', companyId: fact.company })
    else post(a, { type: 'fact', fact })
  }

  const markUncertain = (a: Attempt) => {
    if (a.uncertain && a.storedUncertain) return
    a.uncertain = true
    a.storedUncertain = true
    writeFact(a, 'uncertain')
  }

  /** Removes the key from memory; a drop storage refused stays owed (`owedTombstones`). */
  const release = (a: Attempt) => {
    if (a.shared && a.unstored.includes('dropped')) owedTombstones.current.add(a.key)
    ended.current.add(a.key)
    attempts.current.delete(a.key)
  }

  const forget = (a: Attempt) => {
    a.put?.abort()
    release(a)
  }

  /** Nothing is owed: the record and its facts go, the drop stays as a tombstone (D3). */
  const settle = (a: Attempt) => {
    if (a.shared && userRef.current !== '' && !a.settled) {
      removeCreationAttempt(userRef.current, a.key)
      post(a, { type: 'removed' })
    }
    a.settled = true
    const completing = a.completion !== null && a.completion.stage !== 'done' && a.eligible && !a.dropped
    if (!completing) forget(a)
  }

  /**
   * Reads the key's drop before a retry or a selection. `true` and `false` are answers;
   * `null` is unknown, never "not dropped" (D4, D6). A private attempt skips the read.
   */
  const dropRead = (a: Attempt): boolean | null => {
    if (a.dropped || heardDropped.current.has(a.key)) return true
    if (!a.shared || userRef.current === '') return false
    return isCreationDropped(userRef.current, a.key)
  }

  const drop = (a: Attempt) => {
    if (a.dropped) return
    a.dropped = true
    a.eligible = false
    writeFact(a, 'dropped')
  }

  /**
   * A refresh for another page's company, which lists it and selects nothing. A failure
   * leaves "Check again" (the refresh-failed notice) rather than a list that stays stale.
   */
  const passiveRefresh = () => {
    void refreshRef.current().then((clients) => {
      if (!live.current) return
      refreshFailed.current = clients === null
      rerender()
    })
  }

  /** The list must hold the answered id before anything settles or is selected (D3, D6). */
  const complete = async (a: Attempt) => {
    const companyId = a.outcome?.kind === 'company' ? a.outcome.id : null
    if (companyId === null) return
    a.completion = { companyId, stage: 'refreshing' }
    rerender()
    const clients = await refreshRef.current()
    if (!live.current || a.completion?.companyId !== companyId) return
    if (clients === null) {
      a.completion.stage = 'failed'
      if (!a.eligible) refreshFailed.current = true
      rerender()
      return
    }
    refreshFailed.current = false
    if (!clients.some((c) => c.id === companyId)) {
      a.completion.stage = 'lacks'
      rerender()
      return
    }
    settleListed(a)
  }

  /** The list holds the company: settle the store, then select if this page may (D6). */
  const settleListed = (a: Attempt) => {
    const completion = a.completion
    if (completion === null) return
    settle(a)
    if (!a.eligible || a.dropped) {
      completion.stage = 'done'
      forget(a)
      rerender()
      return
    }
    const dropped = dropRead(a)
    if (dropped === null) {
      completion.stage = 'held'
      rerender()
      return
    }
    completion.stage = 'done'
    forget(a)
    if (dropped) notices.current.add('cancelled')
    else onCreatedRef.current(completion.companyId)
    rerender()
  }

  /** What one answer for a key proves (D2). The first answer that settles a key wins. */
  const answer = (a: Attempt, result: CreationResult, fromPut: boolean) => {
    if (!attempts.current.has(a.key) || a.outcome !== null) return
    switch (result.status) {
      case 'company':
        a.outcome = { kind: 'company', id: result.company.id }
        writeFact(a, { company: result.company.id })
        if (!fromPut && openRef.current) notices.current.add('alreadyCreated')
        void complete(a)
        return
      case 'cancelled':
        a.outcome = { kind: 'none' }
        writeFact(a, 'void')
        if (fromPut && !a.dropped) notices.current.add('cancelled')
        settle(a)
        rerender()
        return
      case 'gone':
        a.outcome = { kind: 'none' }
        writeFact(a, 'void')
        settle(a)
        rerender()
        return
      case 'duplicate':
        // Terminal: the refusal is recorded against the key, which is spent and never
        // creates later. The list is read again, since the name it clashed with may be a
        // company this page has not listed yet.
        a.outcome = { kind: 'none' }
        writeFact(a, 'void')
        if (a.eligible && !a.dropped) duplicateName.current = a.name
        settle(a)
        passiveRefresh()
        rerender()
        return
      case 'noRoute':
        if (!fromPut) return
        if (a.mintedHere && !a.uncertain) {
          notices.current.add('unavailable')
          if (a.shared) drop(a)
          else forget(a)
        } else {
          a.oldBackend = true
        }
        rerender()
        return
      case 'unknown':
        if (fromPut) markUncertain(a)
        rerender()
    }
  }

  /** One `PUT` of the key: the previous one aborted, a pending timer started (D2). */
  const send = (a: Attempt) => {
    a.put?.abort()
    const controller = new AbortController()
    owned.current.add(controller)
    a.put = controller
    a.pendingFired = false
    a.oldBackend = false
    const timer = later(CREATE_PENDING_MS, () => {
      if (a.put !== controller) return
      a.pendingFired = true
      markUncertain(a)
      rerender()
    })
    rerender()
    void putCompanyCreation(a.key, a.name, { signal: controller.signal }).then((result) => {
      owned.current.delete(controller)
      if (!live.current) return
      cancelTimer(timer)
      if (a.put === controller) a.put = null
      if (controller.signal.aborted && result.status === 'unknown') return
      answer(a, result, true)
    })
  }

  /**
   * One `DELETE`, aborted at its deadline, and never two open for a key (D4). `onResult`
   * takes every answer but `unknown`, which goes to `onUnanswered`.
   */
  const sendDelete = (a: Attempt, onResult: (result: CreationResult) => void, onUnanswered: () => void) => {
    if (a.del !== null) return
    const controller = new AbortController()
    owned.current.add(controller)
    a.del = controller
    const deadline = later(CANCEL_SEND_MS, () => controller.abort())
    void deleteCompanyCreation(a.key, { signal: controller.signal }).then((result) => {
      owned.current.delete(controller)
      cancelTimer(deadline)
      if (a.del === controller) a.del = null
      if (!live.current) return
      if (result.status === 'unknown') onUnanswered()
      else onResult(result)
    })
  }

  const sendCancel = (a: Attempt, onUnanswered: () => void) => {
    sendDelete(a, (result) => {
      if (result.status !== 'noRoute') answer(a, result, false)
    }, onUnanswered)
  }

  /**
   * A successful list lacked the answered id. On a key that created a company a `DELETE`
   * cancels and creates nothing; it reads the outcome back (D4). `410`: the company is gone,
   * and the record settles. `200`: it exists, and an explicit "Check again" refreshes at once.
   */
  const readBack = (a: Attempt, explicit: boolean) => {
    sendDelete(a, (result) => {
      if (result.status === 'gone') {
        a.outcome = { kind: 'none' }
        writeFact(a, 'void')
        if (a.completion !== null) a.completion.stage = 'done'
        settle(a)
        forget(a)
        rerender()
        return
      }
      if (result.status !== 'company') return
      // The company exists and the list was stale: refresh now when the reader asked, and
      // at the next opening otherwise.
      if (explicit) void complete(a)
      else if (a.completion !== null) a.completion.stage = 'failed'
    }, () => {})
  }

  const cancelBurst = (a: Attempt, tries: number) => {
    sendCancel(a, () => {
      if (tries > 1 && attempts.current.has(a.key) && a.outcome === null) {
        later(CANCEL_GAP_MS, () => cancelBurst(a, tries - 1))
      }
    })
  }

  /** Adds or updates what this page knows from a stored attempt (mount, a storage event). */
  /** `now`: a storage event, which starts a completion at once; a mount or an opening leaves it to reconciliation (D4). */
  const learn = (stored: StoredAttempt, now = false) => {
    let a = attempts.current.get(stored.key)
    if (a === undefined) {
      a = blank(stored.key, stored.name, stored.at)
      // A page that did not mint the key cannot know what earlier sends did (D2).
      a.uncertain = true
      attempts.current.set(a.key, a)
      if (!stored.uncertain && Date.now() - stored.at < ELSEWHERE_MS) {
        later(ELSEWHERE_MS - (Date.now() - stored.at), rerender)
      }
    }
    a.storedUncertain = a.storedUncertain || stored.uncertain
    if (stored.dropped || heardDropped.current.has(stored.key)) {
      a.dropped = true
      a.eligible = false
    }
    if (stored.outcome?.kind === 'company' && now) adoptCompany(a, stored.outcome.id)
    else if (stored.outcome !== null && a.outcome === null) a.outcome = stored.outcome
  }

  /**
   * A company outcome learned from storage or the channel, rather than from this page's own
   * answer. It takes the one completion path an answer takes, once: whichever of storage,
   * the channel and the HTTP answer arrives first starts it, and the others find the
   * outcome set (D3, D6).
   */
  const adoptCompany = (a: Attempt, companyId: string) => {
    if (a.outcome !== null) return
    a.outcome = { kind: 'company', id: companyId }
    if (a.completion === null) void complete(a)
  }

  /**
   * Reads the inventory once and merges it into memory. `null` means unreadable, which is
   * never taken as "nothing stored": memory, and every fact already learned, is kept as is.
   */
  const loadInventory = (reason: 'mount' | 'opening' | 'checkAgain' | 'storage'): StoredAttempt[] | null => {
    const id = userRef.current
    if (id === '') return null
    const stored = loadCreationAttempts(id)
    if (stored === null) {
      inventory.current = 'unknown'
      if (reason === 'opening') inventoryFailures.current.opening = true
      if (reason === 'checkAgain') inventoryFailures.current.checkAgain = true
      return null
    }
    inventory.current = 'known'
    // A successful read closes the gate that "Add anyway" waits behind (D4).
    inventoryFailures.current = { opening: false, checkAgain: false }
    for (const s of stored) learn(s, reason === 'storage')
    for (const a of attempts.current.values()) {
      if (a.outcome?.kind === 'none' && a.shared) settle(a)
    }
    return stored
  }

  // Storage, the channel and the teardown callback, per user. Closed with every timer when
  // the user changes or App unmounts, so nothing outlives the session it was opened for.
  useEffect(() => {
    live.current = true
    const id = enabled ? userId ?? '' : ''
    if (id === '') return () => { live.current = false }
    attempts.current = new Map()
    owedTombstones.current = new Set()
    ended.current = new Set()
    heardDropped.current = new Set()
    loadInventory('mount')
    pruneOrphanFacts(id)
    rerender()

    const applyMessage = (raw: unknown) => {
      const m = messageOf(raw, id)
      if (m === null) return
      let a = attempts.current.get(m.key)
      if (m.type === 'attempt') {
        if (a !== undefined || ended.current.has(m.key)) return
        // Messages can arrive out of order: the key may have settled, or been dropped, by
        // the time its attempt message lands. The store says which, and a failed read falls
        // back to what the message says, as a read that fails does everywhere else (D4).
        if (isCreationDropped(id, m.key) === true) {
          ended.current.add(m.key)
          return
        }
        const stored = loadCreationAttempts(id)
        if (stored !== null) {
          const entry = stored.find((s) => s.key === m.key)
          if (entry === undefined) return
          learn(entry, true)
        } else {
          learn({ key: m.key, name: m.name, at: m.at, uncertain: false, dropped: false, outcome: null })
        }
        rerender()
        return
      }
      if (a === undefined) {
        // A terminal fact for a key not yet heard of: its attempt message is late, or the
        // other page settled it before this one heard anything. A company, or a settlement
        // that may have been one, still owes this page's list a refresh (D3), without
        // bringing the attempt back or making this page eligible to select.
        if (m.type === 'removed' || (m.type === 'fact' && (m.fact === 'void' || m.fact === 'dropped'))) ended.current.add(m.key)
        if (m.type === 'fact' && m.fact === 'dropped') heardDropped.current.add(m.key)
        if (m.type === 'company' || m.type === 'removed') passiveRefresh()
        return
      }
      if (m.type === 'fact' && m.fact === 'uncertain') a.storedUncertain = true
      if (m.type === 'fact' && m.fact === 'dropped') {
        heardDropped.current.add(a.key)
        a.dropped = true
        a.eligible = false
      }
      if (m.type === 'fact' && m.fact === 'void' && a.outcome === null) a.outcome = { kind: 'none' }
      // The company is named by the server's answer for this key, through another page. A
      // passive page refreshes and settles; an eligible one completes as for its own answer,
      // since the press that made it eligible was made here (D6).
      if (m.type === 'company') adoptCompany(a, m.companyId)
      if (m.type === 'removed' || (m.type === 'fact' && m.fact === 'void')) {
        // `void` is an outcome: no company. `removed` is only another page's cleanup, and an
        // attempt the reader pressed a button for here stays until its own outcome is
        // handled, as the storage path keeps it (D3, D6).
        const waiting = a.eligible && !a.dropped && a.completion?.stage !== 'done'
        const keep = m.type === 'removed' ? waiting : waiting && a.completion !== null
        a.settled = true
        if (!keep && a.put === null) {
          release(a)
          passiveRefresh()
        }
      }
      rerender()
    }

    let bc: BroadcastChannel | null = null
    if (typeof BroadcastChannel !== 'undefined') {
      bc = new BroadcastChannel(`grovus-company-create.${id}`)
      bc.addEventListener('message', (event) => applyMessage((event as MessageEvent).data))
    }
    channel.current = bc

    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && !event.key.startsWith(`grovus.companyCreate.${id}.`)) return
      // Another page recorded a company for a key this page never heard of: list it (D3).
      // A company for a known attempt is adopted here, before any removal below can release
      // it: the event carries the value even when the record is already gone (D6).
      const companyItem = `grovus.companyCreate.${id}.company.`
      if (event.key?.startsWith(companyItem) && event.newValue) {
        const known = attempts.current.get(event.key.slice(companyItem.length))
        if (known === undefined) passiveRefresh()
        else adoptCompany(known, event.newValue)
      }
      const before = new Set([...attempts.current.values()].filter((a) => a.shared && !a.mintedHere).map((a) => a.key))
      const snapshot = loadInventory('storage')
      if (snapshot === null) {
        // Unreadable is not absent: nothing remembered is removed on a failed read.
        rerender()
        return
      }
      const stored = new Set(snapshot.map((s) => s.key))
      for (const key of before) {
        const a = attempts.current.get(key)
        // Remote cleanup never releases an attempt the reader pressed a button for here: its
        // eligibility is kept until its outcome is handled (D3, D6).
        if (a !== undefined && !stored.has(key) && a.put === null && !a.eligible) {
          release(a)
          passiveRefresh()
        }
      }
      rerender()
    }
    window.addEventListener('storage', onStorage)

    const offReset = onBeforeAuthReset(() => {
      for (const a of attempts.current.values()) {
        if (a.put !== null) markUncertain(a)
      }
    })

    const pending = timers.current
    const requests = owned.current
    return () => {
      // App can unmount without the session's work being reset (a failed focus recheck):
      // a create in flight is marked uncertain, as teardown does, and every request the
      // hook owns is aborted with its timer, so none outlives it (D2, D4).
      for (const a of attempts.current.values()) {
        if (a.put !== null) markUncertain(a)
      }
      for (const controller of requests) controller.abort()
      requests.clear()
      live.current = false
      offReset()
      window.removeEventListener('storage', onStorage)
      bc?.close()
      channel.current = null
      for (const timer of pending) clearTimeout(timer)
      pending.clear()
    }
    // The effect is the per-user session of this hook; its helpers read refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, enabled])

  /** Each change of the switcher from closed to open reconciles once (D4). */
  const reconcile = useCallback((reason: 'opening' | 'checkAgain') => {
    const id = userRef.current
    // "Add anyway" follows a failed read on this opening and then a failed "Check again",
    // not failures left over from an earlier opening (D4).
    if (reason === 'opening') inventoryFailures.current = { opening: false, checkAgain: false }
    if (id !== '' && (inventory.current === 'unknown' || reason === 'opening')) loadInventory(reason)
    if (id !== '') pruneOrphanFacts(id)
    if (id !== '') {
      for (const key of [...owedTombstones.current]) {
        if (setCreationFact(id, key, 'dropped')) owedTombstones.current.delete(key)
      }
    }
    for (const a of [...attempts.current.values()]) {
      if (a.unstored.length > 0 && a.shared && id !== '') {
        a.unstored = a.unstored.filter((fact) => !setCreationFact(id, a.key, fact))
      }
      if (a.completion?.stage === 'held') {
        const dropped = dropRead(a)
        if (dropped === true) {
          a.completion.stage = 'done'
          notices.current.add('cancelled')
          forget(a)
        } else if (dropped === false) {
          a.completion.stage = 'done'
          const companyId = a.completion.companyId
          forget(a)
          onCreatedRef.current(companyId)
        }
        continue
      }
      if (a.checkFailed) {
        const dropped = dropRead(a)
        if (dropped === true) {
          forget(a)
          notices.current.add('cancelled')
        } else if (dropped === false) {
          a.checkFailed = false
        }
      }
      if (a.outcome === null && a.dropped) {
        sendCancel(a, () => {})
        continue
      }
      if (a.outcome?.kind === 'company') {
        if (a.completion?.stage === 'lacks') {
          readBack(a, reason === 'checkAgain')
          continue
        }
        if (a.completion === null || a.completion.stage === 'failed') void complete(a)
      }
    }
    // A refresh that failed is retried on "Check again" and on the next opening alike.
    if (refreshFailed.current) passiveRefresh()
    rerender()
    // Reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const wasOpen = useRef(switcherOpen)
  useEffect(() => {
    if (switcherOpen && !wasOpen.current) reconcile('opening')
    if (!switcherOpen && wasOpen.current) {
      // Informational notices last while the switcher is open, not across openings.
      for (const notice of ['unavailable', 'alreadyCreated', 'cancelled'] as const) notices.current.delete(notice)
      rerender()
    }
    wasOpen.current = switcherOpen
  }, [switcherOpen, reconcile])

  const onCreate = useCallback((name: string) => {
    const trimmed = name.trim()
    if (trimmed === '') return
    if (inventory.current === 'unknown' && !anyway.current) return
    for (const notice of ['unavailable', 'alreadyCreated', 'cancelled'] as const) notices.current.delete(notice)
    const id = userRef.current
    const key = crypto.randomUUID()
    const at = Date.now()
    const a = blank(key, trimmed, at)
    a.mintedHere = true
    a.eligible = true
    a.shared = id !== '' && !(inventory.current === 'unknown' && anyway.current) && recordCreationAttempt(id, key, trimmed, at)
    a.degraded = id !== '' && !a.shared
    attempts.current.set(key, a)
    post(a, { type: 'attempt', name: trimmed, at })
    send(a)
    // Reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onRetry = useCallback((key: string) => {
    const a = attempts.current.get(key)
    if (a === undefined || a.outcome !== null) return
    const dropped = dropRead(a)
    if (dropped === null) {
      a.checkFailed = true
      rerender()
      return
    }
    if (dropped) {
      a.dropped = true
      a.eligible = false
      notices.current.add('cancelled')
      rerender()
      return
    }
    a.checkFailed = false
    a.eligible = true
    markUncertain(a)
    send(a)
    // Reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCancel = useCallback((key: string) => {
    const a = attempts.current.get(key)
    if (a === undefined || a.outcome !== null) return
    drop(a)
    cancelBurst(a, CANCEL_TRIES)
    rerender()
    // Reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCheckAgain = useCallback(() => reconcile('checkAgain'), [reconcile])

  const onAddAnyway = useCallback(() => {
    anyway.current = true
    rerender()
  }, [])

  const onDuplicateShown = useCallback(() => {
    if (duplicateName.current === null) return
    duplicateName.current = null
    rerender()
  }, [])

  // The panel's view, derived from the map on every render.
  const now = Date.now()
  const blocks: CreationBlock[] = []
  let holdsCompletion = false
  let keepOpen = false
  let lacks = false
  for (const a of attempts.current.values()) {
    if (a.completion?.stage === 'held') holdsCompletion = true
    // A confirmed company the list does not hold owes the reader "Check again", whether or
    // not this page may select it; eligibility decides only the selection (D4, D6).
    if (a.completion?.stage === 'lacks' || (a.eligible && a.completion?.stage === 'failed')) lacks = true
    if (a.dropped || a.outcome !== null || a.settled) {
      // Also a private attempt the reader cancelled: until its cancel or its outcome settles
      // it, a reload loses the key and what it owes.
      if (a.unstored.length > 0 || (a.degraded && a.outcome === null)) keepOpen = true
      continue
    }
    let state: CreationBlock['state']
    if (a.put !== null && !a.pendingFired) state = 'pendingHere'
    else if (a.mintedHere || a.put !== null || a.eligible || a.storedUncertain || now - a.at >= ELSEWHERE_MS) state = 'unresolved'
    else state = 'elsewhere'
    blocks.push({
      key: a.key,
      name: a.name,
      state,
      oldBackend: a.oldBackend,
      keepOpen: a.degraded || a.unstored.length > 0,
      checkFailed: a.checkFailed,
    })
  }
  const panelNotices: CreationNotice[] = [...notices.current]
  if (lacks) panelNotices.push('notListed')
  if (refreshFailed.current) panelNotices.push('refreshFailed')
  if (holdsCompletion) panelNotices.push('checkFailedCreated')
  if (keepOpen || owedTombstones.current.size > 0) panelNotices.push('keepOpen')
  const unknown = inventory.current === 'unknown'
  if (unknown && !anyway.current) panelNotices.push('inventoryFailed')

  return {
    panel: {
      blocks,
      notices: panelNotices,
      canAdd: blocks.length === 0 && (!unknown || anyway.current),
      offerAnyway: unknown && !anyway.current && inventoryFailures.current.opening && inventoryFailures.current.checkAgain,
      duplicateName: duplicateName.current,
    },
    onCreate,
    onRetry,
    onCancel,
    onCheckAgain,
    onAddAnyway,
    onDuplicateShown,
  }
}
